// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw } from 'lucide-react';
import { getDivisionColor } from '@/utils/divisionColors';
import { PublicEventHeader } from '@/components/public/PublicEventHeader';
import { usePublicLive, useDivisionParam, formatUpdated } from '@/hooks/usePublicLive';
import { PublicGameSections, formatGameDay } from '@/components/public/PublicGameRow';

const FN = 'rle-public-schedule';
// Day filter lives in the URL (same pattern as division) so shared links keep it.
function useDayParam(): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const day = params.get('day') || 'all';
  const setDay = (value: string) => {
    const next = new URLSearchParams(window.location.search);
    if (!value || value === 'all') next.delete('day');
    else next.set('day', value);
    setParams(next, { replace: true });
  };
  return [day, setDay];
}

const PublicScoreboardPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  // Division is the primary filter and lives in the URL, so the view is shareable.
  const [division, setDivision] = useDivisionParam();
  const [team, setTeam] = useState('all');
  const [day, setDay] = useDayParam();

  const { data, loading, errorCode, lastUpdated, refreshing, refresh } = usePublicLive(
    FN,
    { action: 'get', slug },
    { enabled: !!slug },
  );

  // Teams follow the division filter: when a division is chosen, only its
  // teams appear in the Team select.
  const divisionTeams = useMemo(() => {
    const teams: any[] = data?.teams || [];
    if (division === 'all') return teams;
    return teams.filter((t) => t.division === division);
  }, [data, division]);

  // If the picked team drops out of the narrowed list, fall back to all teams.
  useEffect(() => {
    if (!data || team === 'all') return;
    if (!divisionTeams.some((t: any) => t.id === team)) setTeam('all');
  }, [data, division, team, divisionTeams]);

  // Flat list of all games, refreshed in place by the poll.
  const games = useMemo(() => {
    const flat: any[] = [];
    (data?.days || []).forEach((day: any) =>
      (day.games || []).forEach((g: any) => flat.push(g)),
    );
    return flat;
  }, [data]);

  const visibleGames = useMemo(() => {
    return games
      .filter((g) => day === 'all' || g.date === day)
      .filter((g) => division === 'all' || g.division === division)
      .filter(
        (g) => team === 'all' || g.team_a_id === team || g.team_b_id === team,
      );
  }, [games, division, team, day]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCode === 'NOT_PUBLISHED') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          This event isn't live yet.
        </h1>
      </div>
    );
  }

  if (errorCode === 'NOT_FOUND') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Event not found.</h1>
      </div>
    );
  }

  if (errorCode || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          Could not load scoreboard.
        </h1>
      </div>
    );
  }

  const event = data.event || {};

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <PublicEventHeader
        slug={slug ?? ''} current="scoreboard" division={division}
        event={event} branding={data.branding} tabs={data.tabs || []} teams={data.teams || []}
      />

      {/* Filters */}
      <div className="mb-8 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Division
          </label>
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All divisions</SelectItem>
              {(data.divisions || []).map((d: string) => {
                const c = getDivisionColor(d);
                return (
                  <SelectItem key={d} value={d}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: c.dot }}
                      />
                      {d}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Team
          </label>
          <Select value={team} onValueChange={setTeam}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {divisionTeams.map((t: any) => (
                <SelectItem key={t.id} value={t.id}>
                  {division === 'all' && t.division ? `${t.name} · ${t.division}` : t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Day
          </label>
          <Select value={day} onValueChange={setDay}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All days</SelectItem>
              {(data.days || []).map((d: any) => (
                <SelectItem key={d.date} value={d.date}>
                  {formatGameDay(d.date)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <span className="pb-2 text-xs text-muted-foreground">
          Updated {formatUpdated(lastUpdated)}
        </span>
      </div>

      <PublicGameSections games={visibleGames} slug={slug ?? ''} upcomingLimit={12} />
    </div>
  );
};

export default PublicScoreboardPage;
